import { useRouter } from "next/router";
import { useTranslator } from "@eo-locale/react";

import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import { toastDuration } from "@/components/places/constants";
import { useToast } from "@/storybook-components";
import { postMarker, updateMarker } from "@/redux/features/place-marker/thunk";
import {
  resetFilterCategories,
  setFilteredCategoriesData,
  updateMarker as updateMarkerDetails
} from "@/redux/features/place-markers/slice";
import { setPinDetails } from "@/redux/features/place/slice";
import {
  resetMarker,
  setMarkerError
} from "@/redux/features/place-marker/slice";
import {
  clearAllErrors,
  resetAnswers,
  setError,
  setSaving
} from "@/redux/features/place-form/slice";
import { setView } from "@/redux/features/place-right-panel/slice";
import { validate as validateSingleLineQuestion } from "@/components/places/survey/questions/singleline-question";
import { validate as validateRadioButtonQuestion } from "@/components/places/survey/questions/singleline-question";
import { validate as validateDropdownQuestion } from "@/components/places/survey/questions/dropdown-question";
import { validate as validateEssayQuestion } from "@/components/places/survey/questions/essay-question";
import { validate as validateCheckboxQuestion } from "@/components/places/survey/questions/checkbox-question";
import { fetchMarkerCategories } from "@/redux/features/place-marker-category/thunk";
import { fetchPlace } from "@/redux/features/place/thunk";
import { fileUpload } from "@/models";
import { fetchPinDetails } from "@/redux/features/place-markers/thunk";
import { fetchAuthToken } from "@/helpers/fetch-auth-token";

const usePinFlow = () => {
  const toast = useToast();
  const router = useRouter();

  const dispatch = useAppDispatch();
  const { marker } = useAppSelector((state) => state.placesMarkerSlice);
  const { filteredCategoriesData } = useAppSelector(
    (state) => state.placeMarkersSlice
  );

  const { imageVisibility } = useAppSelector((state) => state.place.place);
  const { translate } = useTranslator();

  const { questions, answers } = useAppSelector((state) => state.placesForm);
  const placesId = router.query.placesId as string;
  const projectId = router.query.projectId as string;
  const { baseUrl } = useAppSelector((state) => state.adapter.adapter);
  const user = useAppSelector((state) => state.user);
  const anonymousUser = useAppSelector((state) => state.anonymousUser);
  const { token } = fetchAuthToken({ user, anonymousUser });
  const markerResponse = useAppSelector((state) => state.placesForm.answers);

  const { imageDetails } = useAppSelector((state) => state.placesForm);

  const resetData = () => {
    dispatch(resetAnswers());
    dispatch(clearAllErrors());
    dispatch(resetMarker());
    dispatch(resetFilterCategories());
    dispatch(setPinDetails({}));
  };

  const validateQuestion: any = (answer, question) => {
    const Validation: {
      // eslint-disable-next-line no-unused-vars
      [key in any]: any;
    } = {
      // Section & Image questions have no answers,
      // so just return empty promises indicating no validation erros
      EssayQuestion: validateEssayQuestion,
      CheckBoxQuestion: validateCheckboxQuestion,
      FileQuestion: async () => [],
      SingleLineQuestion: validateSingleLineQuestion,
      RadioButtonQuestion: validateRadioButtonQuestion,
      DropDownQuestion: validateDropdownQuestion
    };
    return Validation[question.type]?.call(undefined, answer, question);
  };

  const uploadImage = async (imageBlob) => {
    const file = await fetch(imageBlob).then((res) => res.blob());
    const uploadedFile = await fileUpload(token, baseUrl, file);

    return uploadedFile.url;
  };

  const validateSurveyQuestions = async () => {
    let isError = false;

    for (let i = 0; i < questions.length; i++) {
      const question = questions[i];
      const error = await validateQuestion(answers[question.id], question);

      if (error?.length) {
        isError = true;
        dispatch(
          setError({
            questionId: questions[i].id,
            type: "question",
            error: "blank"
          })
        );
      }
    }
    return isError;
  };

  const editMarker = async (marker: any) => {
    const { fileUrl: imageUrl } = imageDetails;
    let isError = false;
    isError = await validateSurveyQuestions();

    if (!isError) {
      dispatch(setSaving(true));

      let mediaUrl: any = "";

      const imageQuestionid = questions.find(
        (question) => question.type === "FileQuestion"
      )?.id;

      if (imageUrl) {
        const isLocalImage = imageDetails.fileUrl.includes("base64");
        const uploadedFileUrl = isLocalImage
          ? await uploadImage(imageUrl)
          : imageUrl;

        mediaUrl = {
          [imageQuestionid]: {
            "file-content-type": imageDetails.type,
            "file-name": imageDetails.name,
            "file-size": imageDetails.size,
            "file-url": uploadedFileUrl
          }
        };
      } else {
        mediaUrl = {
          [imageQuestionid]: {
            "file-content-type": "",
            "file-name": "",
            "file-size": "",
            "file-url": ""
          }
        };
      }

      const markerObject = {
        projectId,
        placesId,
        markerId: marker.id,
        body: {
          marker: {
            address: marker.address,
            lng: marker.lng, // for future -> add the center co-ordinates if the latitude and longitude are not there
            lat: marker.lat,
            categoryId: marker.categoryId
          }
        }
      };

      markerObject.body["marker-response"] = {
        ...mediaUrl,
        ...markerResponse
      };

      try {
        const response = await dispatch(updateMarker(markerObject));
        if (response.meta.requestStatus === "fulfilled") {
          dispatch(updateMarkerDetails(marker));
          await dispatch(fetchPinDetails({ placesId, projectId }));

          dispatch(resetFilterCategories());

          dispatch(setSaving(false));
          dispatch(fetchMarkerCategories({ placesId, projectId }));
          resetData();
          // Refresh responses count
          dispatch(fetchPlace({ placesId, projectId }));
          dispatch(setView("activity-feed"));
          showToast({
            description: translate("places.contributionUpdate"),
            status: "success"
          });

          return;
        }
        if (response.meta.requestStatus === "rejected") {
          dispatch(setSaving(false));
          showToast({
            description: translate("places.contributionFailure"),
            status: "error"
          });
          return;
        }
        // eslint-disable-next-line no-unused-vars
      } catch (err) {
        /* empty */
      }
    }
  };

  const createMarker = async () => {
    let isPinError = false;

    // convert data uri to image file and than get the uploaded image url from BE
    let mediaUrl: any = "";

    if (!marker.pinIcon) {
      isPinError = true;
      await dispatch(setMarkerError("empty"));
    }

    dispatch(
      setFilteredCategoriesData({
        ...filteredCategoriesData,
        sortOrder: "newest"
      })
    );

    let isSurveyError = await validateSurveyQuestions();

    if (!isPinError && !isSurveyError) {
      dispatch(setSaving(true));

      const { fileUrl: imageUrl } = imageDetails;
      if (imageUrl && imageVisibility) {
        const file = await fetch(imageUrl).then((res) => res.blob());
        const uploadedFile = await fileUpload(token, baseUrl, file);

        mediaUrl = {
          [imageDetails.questionId]: {
            "file-content-type": imageDetails.type,
            "file-name": imageDetails.name,
            "file-size": imageDetails.size,
            "file-url": uploadedFile.url
          }
        };
      }

      const markerObject = {
        projectId,
        placesId,
        body: {
          marker: {
            address: marker.address,
            lng: marker.lng,
            lat: marker.lat,
            categoryId: marker.categoryId
          }
        }
      };

      /**
       * If the making comment is mandatory is enabled -> optional comment will be false -> send in the response
       * If the making comment is mandatory is disabled -> optional comment will be true -> do not send in the response
       * If the making comment is mandatory is disabled -> optional comment will be true -> and if the marker response is present -> send in the response
       */

      // if optional comment is false , then response should be present

      markerObject.body["marker-response"] = {
        ...mediaUrl,
        ...markerResponse
      };

      // eslint-disable-next-line no-undef
      if (!navigator.onLine) {
        dispatch(setSaving(false));

        await toast({
          description: translate("places.internetFailure"),
          title: "Something went wrong",
          status: "error",
          duration: toastDuration,
          isClosable: true,
          position: "bottom"
        });

        return;
      }

      const response = await dispatch(postMarker(markerObject));

      if (response.meta.requestStatus === "rejected") {
        dispatch(setSaving(false));

        showToast({
          description: translate("places.contributionFailure"),
          status: "error"
        });

        return;
      }

      if (response.meta.requestStatus === "fulfilled") {
        await dispatch(fetchPinDetails({ placesId, projectId }));
        dispatch(setSaving(false));

        showToast({
          description: translate("places.contributionSuccess"),
          status: "success"
        });

        resetData();
        dispatch(fetchMarkerCategories({ placesId, projectId }));

        // Refresh responses count
        dispatch(fetchPlace({ placesId, projectId }));

        // set the view to pin details page
        dispatch(setView("activity-feed"));
        return;
      }
    }
  };

  const showToast = ({ description, status }) => {
    toast({
      description,
      status,
      duration: toastDuration,
      isClosable: true,
      position: "bottom"
    });
  };

  return {
    createMarker,
    validateQuestion,
    editMarker,
    validateSurveyQuestions
  };
};

export default usePinFlow;
